import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getTimestampDisplay } from "@/features/changelog/lib/changelog-display";

/** Enough to show the shape of the window without turning the sheet into a log. */
const COLLAPSED_COVERAGE_COUNT = 5;

interface ChangelogCoverageListProps {
  /** The deploys this roundup rolled up, newest first. */
  entries: Array<{ versionTag: string; deployedAt: string }>;
}

/**
 * Which deploys a roundup swallowed. A wrapped row of version chips says how
 * many there were and nothing else; pairing each tag with the day it shipped
 * answers the question the list is actually opened for — what period this
 * roundup speaks for.
 */
export function ChangelogCoverageList({ entries }: ChangelogCoverageListProps) {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  if (entries.length === 0) return null;

  const hasOverflow = entries.length > COLLAPSED_COVERAGE_COUNT;
  const visible =
    hasOverflow && !isExpanded ? entries.slice(0, COLLAPSED_COVERAGE_COUNT) : entries;

  return (
    <section className="flex flex-col gap-2 border-t border-border-subtle pt-4">
      <h3 className="m-0 text-[11px] uppercase tracking-wider text-muted-foreground">
        {t("admin.changelog.roundups.covers", "Covers")}
      </h3>

      <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
        {visible.map((entry) => (
          <li key={entry.versionTag} className="flex items-baseline gap-3 text-xs">
            <span className="w-20 shrink-0 font-mono text-foreground">
              {entry.versionTag}
            </span>
            <span className="tabular-nums text-muted-foreground">
              {getTimestampDisplay(entry.deployedAt, i18n.language).date}
            </span>
          </li>
        ))}
      </ul>

      {hasOverflow ? (
        <button
          type="button"
          onClick={() => setIsExpanded((previous) => !previous)}
          className="cursor-pointer self-start text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
        >
          {isExpanded
            ? t("admin.changelog.roundups.coversLess", "Show less")
            : t("admin.changelog.roundups.coversAll", "Show all {{count}}", {
                count: entries.length,
              })}
        </button>
      ) : null}
    </section>
  );
}
